import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import React from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'
import { RectangleNetworkBackground } from '@root/shared/ui'
import { InfoMessage } from '@root/shared/ui-kit'

import { tiktokPostOembed } from '../atoms/tiktok-post-oembed'
import { TiktokKnownSocialPlatformResource } from '../model/known-social-platform-resource'

import { SocialIframeEmbed } from './social-iframe-embed'

import type * as KnownSocialPlatformResource from '../model/known-social-platform-resource'

interface SocialTiktokEmbedProps {
  onLoad: () => void
  resource: KnownSocialPlatformResource.TiktokKnownSocialPlatformResource<'tiktok'>
}

export const SocialTiktokEmbed = React.memo(
  function SocialTiktokEmbed({ resource, onLoad }: SocialTiktokEmbedProps) {
    const model = useMemo(() => tiktokPostOembed({ resource }), [resource])
    const [requestState] = useAtom(model.requestModel)
    const refresh = useAction(model.requestModel.execute)

    useMount(() => {
      onLoad()
    })

    return AsyncStateN.matchWaiting(requestState, {
      Waiting: () => (
        <Root>
          <Loader />
        </Root>
      ),
      Error: () => (
        <Root>
          <InfoMessage intent={'error'} onRetry={refresh} />
        </Root>
      ),
      Success: (oembed) => {
        const citeUrl = oembed.citeUrl

        if (!citeUrl) {
          return (
            <Root>
              <InfoMessage intent={'error'} onRetry={refresh} />
            </Root>
          )
        }

        const iframeResource =
          TiktokKnownSocialPlatformResource.fromString(citeUrl)

        if (
          !iframeResource ||
          !TiktokKnownSocialPlatformResource.isIframeResource(iframeResource)
        ) {
          return (
            <Root>
              <InfoMessage intent={'error'} onRetry={refresh} />
            </Root>
          )
        }

        return <SocialIframeEmbed resource={iframeResource} onLoad={onLoad} />
      },
    })
  },
  (prevProps, nextProps) => {
    return prevProps.resource.url === nextProps.resource.url
  },
)

const Root = styled.div`
  ${RectangleNetworkBackground}

  width: 350px;
  min-height: fit-content;
  position: relative;
  border: 1px solid var(--background-6);
  border-radius: 6px;
  overflow: hidden;
`

const Loader = () => (
  <LoaderRoot>
    <CircularLoader size={24} />
  </LoaderRoot>
)

const LoaderRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 160px;
`
